import { useState, useEffect } from "react";
import { Activity, Zap, Leaf, CheckCircle, RefreshCw, Settings } from "lucide-react";
import axios, { API } from "@/utils/api";
import { useT } from "@/contexts/LanguageContext";

const AlertsView = ({ clientId, alerts, onRefresh }) => {
  const t = useT();
  const [thresholds, setThresholds] = useState({ min_power_factor: 0.85, max_energy_kwh: 100000, max_co2_kg: 5000 });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchThresholds = async () => {
      try {
        const res = await axios.get(`${API}/clients/${clientId}/thresholds`);
        setThresholds(res.data);
      } catch (error) {
        console.error("Error fetching thresholds:", error);
      }
    };
    fetchThresholds();
  }, [clientId]);

  const handleSaveThresholds = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/clients/${clientId}/thresholds`, thresholds);
      onRefresh();
    } catch (error) {
      console.error("Error saving thresholds:", error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="alerts-view">
      <div className="page-header flex-between">
        <div>
          <h2 className="page-title">{t("threshold_alerts")}</h2>
          <p className="page-subtitle">{alerts.length > 0 ? `${alerts.length} ${t("alerts").toLowerCase()}` : t("no_alerts")}</p>
        </div>
        <button className="btn btn-primary" onClick={onRefresh} data-testid="refresh-alerts-btn">
          <RefreshCw size={18} />
          {t("refresh")}
        </button>
      </div>

      <div className="card mb-lg" data-testid="threshold-settings">
        <h4 className="card-title"><Settings size={20} /> {t("threshold_settings")}</h4>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">{t("pf_threshold")}</label>
            <input type="number" step="0.01" className="form-input" value={thresholds.min_power_factor}
              onChange={(e) => setThresholds({...thresholds, min_power_factor: parseFloat(e.target.value)})}
              data-testid="threshold-pf" />
          </div>
          <div className="form-group">
            <label className="form-label">{t("energy_threshold")}</label>
            <input type="number" step="100" className="form-input" value={thresholds.max_energy_kwh}
              onChange={(e) => setThresholds({...thresholds, max_energy_kwh: parseFloat(e.target.value)})}
              data-testid="threshold-energy" />
          </div>
          <div className="form-group">
            <label className="form-label">{t("co2_threshold")}</label>
            <input type="number" step="100" className="form-input" value={thresholds.max_co2_kg}
              onChange={(e) => setThresholds({...thresholds, max_co2_kg: parseFloat(e.target.value)})}
              data-testid="threshold-co2" />
          </div>
        </div>
        <button className="btn btn-primary mt-md" onClick={handleSaveThresholds} disabled={saving} data-testid="save-thresholds-btn">
          {saving ? t("saving") : t("save_thresholds")}
        </button>
      </div>

      {alerts.length === 0 ? (
        <div className="card" style={{ borderLeft: "4px solid var(--success)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "var(--space-sm)" }}>
            <CheckCircle size={20} style={{ color: "var(--success)" }} />
            <span>{t("no_alerts")}</span>
          </div>
        </div>
      ) : (
        <div className="alerts-list">
          {alerts.map((alert, idx) => (
            <div key={idx} className={`alert-card severity-${alert.severity}`} data-testid={`alert-item-${idx}`}>
              <div className="alert-card-header">
                <div className="alert-card-icon">
                  {alert.type === "power_factor" ? <Activity size={20} /> : alert.type === "energy" ? <Zap size={20} /> : <Leaf size={20} />}
                </div>
                <div className="alert-card-info">
                  <div className="alert-card-dept">{alert.department}</div>
                  <div className="alert-card-message">{alert.message}</div>
                </div>
                <span className={`badge badge-${alert.severity === "high" ? "na" : "warning"}`}>
                  {alert.severity === "high" ? t("high") : t("medium")}
                </span>
              </div>
              <div className="alert-card-details">
                <span>Value: <strong>{typeof alert.value === "number" ? alert.value.toLocaleString() : alert.value}</strong></span>
                <span>Threshold: <strong>{typeof alert.threshold === "number" ? alert.threshold.toLocaleString() : alert.threshold}</strong></span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AlertsView;
